import { useQuery } from "@apollo/client";
import {
  Badge,
  Card,
  Drawer,
  LoadingOverlay,
  ScrollArea,
  Select,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ADMIN_LOAN_PRODUCTS, LOAN_PAYMENTS } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";

const LoanPayments = () => {
  const [size, setSize] = useState("10");
  const [opened, setOpened] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loanAccountFilter, setLoanAccountFilter] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState(null);
  const [productFilter, setProductFilter] = useState(null);

  const { data: productsData, loading: productsLoading } = useQuery(
    ADMIN_LOAN_PRODUCTS,
    {
      variables: {
        first: 1000,
        page: 1,
        search: "",
        orderBy: [
          {
            column: "NAME",
            order: "ASC",
          },
        ],
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message ||
            "Failed to Get Loan Products",
        });
      },
    },
  );

  const { data, loading, refetch } = useQuery(LOAN_PAYMENTS, {
    variables: {
      first: parseInt(size),
      page: activePage,
      search: searchValue,
      loan_account_id: loanAccountFilter || null,
      payment_method: paymentMethodFilter,
      product_id: productFilter,
      orderBy: [
        {
          column: "created_at",
          order: "DESC",
        },
      ],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Get Loan Payments",
      });
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  useEffect(() => {
    if (data) {
      setTotal(data.loanPayments.paginatorInfo.lastPage);
    }
  }, [data, size]);

  useEffect(() => {
    refetch();
  }, []);

  const theme = useMantineTheme();
  const productOptions =
    productsData?.loanProducts?.data?.map((product) => ({
      value: String(product.id),
      label: product.name,
    })) || [];

  const getPaymentMethodColor = (method) => {
    switch (method) {
      case "MANUAL":
        return "blue";
      case "TELEBIRR":
        return "orange";
      case "CBEBIRR":
        return "green";
      case "EBIRR":
        return "cyan";
      case "MPESA":
        return "violet";
      default:
        return "gray";
    }
  };

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "TRX",
      key: "trx",
      width: "30%",
      sortable: true,
      searchable: true,
      render: (rowData) => {
        return <span style={{ fontWeight: 500 }}>{rowData.trx}</span>;
      },
    },
    {
      label: "Amount",
      key: "amount",
      sortable: true,
      searchable: false,
      width: "10%",
      render: (rowData) => {
        return (
          <span style={{ fontWeight: 500, color: "#4CAF50" }}>
            {rowData.amount
              ? `ETB ${parseFloat(rowData.amount).toLocaleString()}`
              : "N/A"}
          </span>
        );
      },
    },
    {
      label: "Penalty Paid",
      key: "penalty_paid",
      sortable: true,
      searchable: false,
      width: "12%",
      render: (rowData) => {
        return (
          <span
            style={{ color: rowData.penalty_paid > 0 ? "#FF6A00" : "inherit" }}
          >
            {rowData.penalty_paid
              ? `ETB ${parseFloat(rowData.penalty_paid).toLocaleString()}`
              : "ETB 0"}
          </span>
        );
      },
    },
    {
      label: "Payment Method",
      key: "payment_method",
      sortable: false,
      searchable: false,
      width: "13%",
      render: (rowData) => {
        return (
          <Badge
            color={getPaymentMethodColor(rowData.payment_method)}
            variant="light"
          >
            {rowData.payment_method || "N/A"}
          </Badge>
        );
      },
    },
    {
      label: "User",
      key: "recorded_by",
      sortable: false,
      searchable: false,
      width: "16%",
      render: (rowData) => {
        return <span>{rowData.user?.name || "System"}</span>;
      },
    },
    {
      label: "Date",
      key: "created_at",
      sortable: true,
      searchable: false,
      width: "13%",
      render: (rowData) => {
        return <span>{new Date(rowData.created_at).toLocaleDateString()}</span>;
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      width: "15%",
      render: (rowData) => {
        return (
          <div style={{ display: "flex", gap: "6px" }}>
            <ActionButton
              label="View"
              onClick={() => handleViewPayment(rowData)}
            >
              <VisibilityIcon style={{ fontSize: "1rem" }} />
            </ActionButton>
          </div>
        );
      },
    },
  ];

  const handleViewPayment = (payment) => {
    setOpened(true);
    setViewData(payment);
  };

  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };

  const [confirmedSearch, setConfirmedSearch] = useState("");

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };

  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={opened}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        padding="xl"
        onClose={() => setOpened(false)}
        position="right"
        size="50%"
        styles={{
          closeButton: {
            color: "black",
            marginTop: "50px",
          },
        }}
      >
        {viewData && (
          <div style={{ padding: "20px" }}>
            <h2 style={{ marginBottom: "20px" }}>Payment Details</h2>
            <div style={{ display: "grid", gap: "15px" }}>
              <div>
                <strong>TRX:</strong> {viewData.trx}
              </div>
              <div>
                <strong>Loan Account ID:</strong> {viewData.loan_account_id}
              </div>
              {viewData.installment_id && (
                <div>
                  <strong>Installment ID:</strong> {viewData.installment_id}
                </div>
              )}
              <div>
                <strong>User ID:</strong> {viewData.user_id}
              </div>
              <div>
                <strong>Amount:</strong>{" "}
                <span style={{ color: "#4CAF50", fontWeight: 600 }}>
                  ETB {parseFloat(viewData.amount).toLocaleString()}
                </span>
              </div>
              <div>
                <strong>Penalty Paid:</strong>{" "}
                <span
                  style={{
                    color: viewData.penalty_paid > 0 ? "#FF6A00" : "inherit",
                    fontWeight: viewData.penalty_paid > 0 ? 600 : 400,
                  }}
                >
                  ETB {parseFloat(viewData.penalty_paid || 0).toLocaleString()}
                </span>
              </div>
              <div>
                <strong>Payment Method:</strong>{" "}
                <Badge color={getPaymentMethodColor(viewData.payment_method)}>
                  {viewData.payment_method}
                </Badge>
              </div>
              {viewData.remark && (
                <div>
                  <strong>Remark:</strong> {viewData.remark}
                </div>
              )}
              <div>
                <strong>Recorded By:</strong> {viewData.recorded_by || "System"}
              </div>
              <div>
                <strong>Payment Date:</strong>{" "}
                {new Date(viewData.created_at).toLocaleString()}
              </div>
            </div>
          </div>
        )}
      </Drawer>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            clearInput={clearInput}
            paginatorInfo={data?.loanPayments.paginatorInfo}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            loading={loading}
            filterData={({ onCardClick }) => (
              <Select
                label="Product Name"
                placeholder="All Products"
                clearable
                searchable
                value={productFilter}
                onChange={(value) => {
                  setProductFilter(value);
                  setActivePage(1);
                  onCardClick(value);
                }}
                data={productOptions}
                disabled={productsLoading}
                withinPortal
              />
            )}
            data={data ? data.loanPayments.data : []}
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default LoanPayments;
